import { useEffect, useState } from "react";
import { cn } from "../lib/utils";

interface Props {
  /** 已選定語系的片語陣列（逐句輪播） */
  phrases: string[];
  /** 完整原句 — prefers-reduced-motion 時靜態顯示；同時作為 aria-label */
  sentence: string;
  /** 每句停留週期（毫秒），預設 2500 */
  intervalMs?: number;
  className?: string;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * 輪動電子字幕：角色標語逐句滑入 → 停留 → 滑出（CSS keyframes，
 * JS 只負責 setInterval 換 index）。reduced-motion 時退回靜態完整句。
 */
export function RotatingTagline({ phrases, sentence, intervalMs = 2500, className }: Props) {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced || phrases.length <= 1) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % phrases.length), intervalMs);
    return () => window.clearInterval(id);
  }, [reduced, phrases.length, intervalMs]);

  if (reduced) {
    return <p className={cn("text-[13.5px] leading-relaxed text-muted-foreground", className)}>{sentence}</p>;
  }

  // 片語陣列中途縮短時防越界（% 取模恆有效）
  const phrase = phrases.length > 0 ? phrases[index % phrases.length] : "";

  return (
    <p className={cn("flex h-6 items-center gap-2 overflow-hidden font-mono text-[13px] tracking-wide", className)}>
      {/* 螢幕報讀器唸完整原句（aria-label 對 <p> 支援度差，改 sr-only） */}
      <span className="sr-only">{sentence}</span>
      <span className="select-none text-warm" aria-hidden>
        ▸
      </span>
      {/* key 換 index 重掛載 → CSS animation 重播一輪（滑入-停留-滑出）；
          duration 由此處 inline style 控制（index.css 的 2.5s 僅為 fallback） */}
      <span
        key={index}
        className="animate-tagline-cycle inline-block text-muted-foreground"
        style={{ animationDuration: `${intervalMs}ms` }}
        aria-hidden
      >
        {phrase}
      </span>
    </p>
  );
}
